import { gql, type TypedDocumentNode } from '@apollo/client';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { BrandXS } from '@sorare/core/src/atoms/typography/index.tsx';
import {
  CornerBadge,
  Position,
} from '@sorare/core/src/components/CornerBadge/index.tsx';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage/index.tsx';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText/index.tsx';

import type { TooltipContent_anyRewardConfigInterface } from '#src/components/boards/HotStreakTile/TooltipContent/__generated__/index.graphql.ts';
import { TooltipContent } from '#src/components/boards/HotStreakTile/TooltipContent/index.tsx';

import type { TaskTooltipContent_thresholdPickerTask } from './__generated__/index.graphql.ts';

type Props = {
  task: TaskTooltipContent_thresholdPickerTask;
  showTargetValue: boolean;
  oldThreshold: number | null;
  originalTarget?: number | null;
  originalRewardConfig?: TooltipContent_anyRewardConfigInterface | null;
};

const PositionedBadge = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  transform: translateY(-50%);
  z-index: 1;
`;

const BadgeDivider = styled.span`
  width: 1px;
  height: var(--intermediate-unit);
  margin: 0 calc(var(--half-unit) / 2);
  background: var(--c-nd-150);
`;

export const TaskTooltipContent = ({
  task,
  showTargetValue,
  oldThreshold,
  originalTarget = null,
  originalRewardConfig = null,
}: Props) => {
  const { rewardConfigs, target, averageMode } = task;
  const hasReducedThreshold =
    !averageMode && !!oldThreshold && oldThreshold > target;

  const [rewardConfig, ...badgeRewardConfigs] = rewardConfigs;

  if (!rewardConfig) {
    return null;
  }

  return (
    <>
      {badgeRewardConfigs.length > 0 && (
        <PositionedBadge>
          <CornerBadge color="var(--c-gradient-limited)" align={Position.RIGHT}>
            <Horizontal center>
              {badgeRewardConfigs.map((badgeRewardConfig, index) => (
                <Horizontal
                  center
                  gap={0}
                  key={badgeRewardConfig.id}
                  style={{ color: 'var(--c-white)' }}
                >
                  {index > 0 && <BadgeDivider />}
                  <RewardConfigImage
                    anyRewardConfig={badgeRewardConfig}
                    size={2}
                    flat={false}
                  />
                  <BrandXS>
                    <RewardConfigText
                      anyRewardConfig={badgeRewardConfig}
                      starsVariant="flat"
                      numberNotation="compact"
                    />
                  </BrandXS>
                </Horizontal>
              ))}
            </Horizontal>
          </CornerBadge>
        </PositionedBadge>
      )}
      <TooltipContent
        target={task.target}
        showTargetValue={showTargetValue}
        hasReducedThreshold={hasReducedThreshold}
        oldThreshold={oldThreshold}
        rewardConfig={rewardConfig}
        originalTarget={originalTarget}
        originalRewardConfig={originalRewardConfig}
      />
    </>
  );
};

TaskTooltipContent.fragments = {
  // for the originalRewardConfig prop
  anyRewardConfigInterface: TooltipContent.fragments.anyRewardConfigInterface,
  thresholdPickerTask: gql`
    fragment TaskTooltipContent_thresholdPickerTask on ThresholdPickerTask {
      id
      target
      averageMode
      rewardConfigs {
        id
        ...TooltipContent_anyRewardConfigInterface
        ...RewardConfigImage_anyRewardConfigInterface
        ...RewardConfigText_anyRewardConfigInterface
      }
    }
    ${TooltipContent.fragments.anyRewardConfigInterface}
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<TaskTooltipContent_thresholdPickerTask>,
};
